import React from "react";
import { toast } from "react-toastify";
import { FileDownloader } from "src/Components";
import UsersDropdown from "src/Components/Dropdown/Users/UsersDropdown";
import GroupTableUI, {
  IGroupTablePersistentStateProps,
} from "./GroupTableUI";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import {
  getPortfolioGroupAccountsDropdown,
  getPortfoOverviewTable,
  getSecuritiesBuyingPowerTable,
  getCoveredCallSummaryTable,
  getDeltaHedgeSummaryTable,
  getDeltaHedgeSegregationDetailsTable,
  getConversionSummaryTable,
  getNonOptionAssetClassPortfolioTable,
  getNakedOptionPortfolioTable,
  getDetailedPortfolioExcel,
} from "src/Requests/portfolioGroups";
import type {
  IPortfoGroupDetailedSearchParams,
  IPortfoOverviewTableReport,
  IPortfoOverviewTableResponse,
  ISecuritiesBuyingPowerTableReport,
  ICoveredCallSummaryTableReport,
  IDeltaHedgeSummaryTableReport,
  IPortfoSegregationDetailsSearchParams,
  IPortfoSegregationDetailsTableReport,
  IConversionSummaryTableReport,
  INonOptionAssetClassPortfolioTableReport,
  INakedOptionPortfolioTableReport,
} from "src/Interfaces/portfolioGroups";
import { ITableReport, Report } from "src/Interfaces/TableInterfaces";
import type { ITableUI } from "src/Tables/TableTemplate/tableTemplate";

const overviewHiddenHeaderNames = new Set([
  "RequiredAction",
  "SurplusDeficit",
  "MinPercent",
  "MaxPercent",
]);

const withoutOverviewAllocationColumns = <ReportType extends ITableReport>(
  reports: Report<ReportType>,
): Report<ReportType> => ({
  ...reports,
  translatedHeaders: reports.translatedHeaders.filter(
    (header) => !overviewHiddenHeaderNames.has(header.headerName),
  ),
});

interface ITableSectionProps<ReportType extends ITableReport> {
  title: string;
  className?: string;
  searchParams: IPortfoGroupDetailedSearchParams;
  getDataTable: (
    searchParam: IPortfoGroupDetailedSearchParams,
    offset: number | null,
    pageSize: number | null,
    sortingType?: string,
  ) => Promise<Report<ReportType>>;
  tableBoxClassName?: string;
  onResponse?: (response: Report<ReportType>) => void;
  TableUIComponent?: React.FC<
    ITableUI<ReportType> & Partial<IGroupTablePersistentStateProps>
  >;
}

const TableSection = <ReportType extends ITableReport>({
  title,
  className = "",
  searchParams,
  getDataTable,
  tableBoxClassName = "max-h-[500px]",
  onResponse,
  TableUIComponent = GroupTableUI,
}: ITableSectionProps<ReportType>) => {
  const collapsedGroupsRef = React.useRef<Set<string>>(new Set());
  const scrollPositionRef = React.useRef({ x: 0, y: 0 });

  return (
    <div
      className={`flex w-full flex-col gap-2 justify-start items-stretch text-right ${className}`}
    >
      <div className="w-full px-3 text-right text-sm font-bold whitespace-nowrap">
        {title}
      </div>
      <TableTemplate<IPortfoGroupDetailedSearchParams, ReportType>
        getDataTable={getDataTable}
        searchParameters={searchParams}
        tableWrapperClassname="w-full justify-start"
        TableUI={({
          reports,
          onAction,
          sortingType,
          setSortingType,
          scrollKey,
        }) => (
          <TableUIComponent
            reports={withoutOverviewAllocationColumns(reports)}
            onAction={onAction}
            sortingType={sortingType}
            setSortingType={setSortingType}
            tableBoxClassName={tableBoxClassName}
            scrollKey={scrollKey}
            persistentCollapsedGroupsRef={collapsedGroupsRef}
            persistentScrollPositionRef={scrollPositionRef}
          />
        )}
        withSort
        refreshTableTime={30000}
        pagesize={200}
        onResponse={onResponse}
      />
    </div>
  );
};

interface ISecuritiesBuyingPowerTableUIProps {
  reports: Report<ISecuritiesBuyingPowerTableReport>;
  onAction?: () => void;
  sortingType?: string;
  setSortingType?: (sortingType: string) => void;
  scrollKey?: string;
}

const SecuritiesBuyingPowerTableUI: React.FC<
  ISecuritiesBuyingPowerTableUIProps
> = ({
  reports,
  onAction,
  sortingType,
  setSortingType,
  scrollKey,
}) => {
  return (
    <div className="securities-buying-power-table w-full">
      <CommonTableUI
        reports={withoutOverviewAllocationColumns(reports)}
        onAction={onAction}
        sortingType={sortingType}
        setSortingType={setSortingType}
        scrollKey={scrollKey}
        tableHeaderClassName="!bg-sky-50 hover:!bg-sky-100"
      />
    </div>
  );
};

interface IDeltaHedgeSummaryTableUIProps {
  reports: Report<IDeltaHedgeSummaryTableReport>;
  onAction?: () => void;
  sortingType?: string;
  setSortingType?: (sortingType: string) => void;
  onRowClick: (row: IDeltaHedgeSummaryTableReport) => void;
  activeRowId?: number;
  tableBoxClassName?: string;
  scrollKey?: string;
}

const DeltaHedgeSummaryTableUI: React.FC<IDeltaHedgeSummaryTableUIProps> = ({
  reports,
  onAction,
  sortingType,
  setSortingType,
  onRowClick,
  activeRowId,
  tableBoxClassName,
  scrollKey,
}) => (
  <CommonTableUI
    reports={reports}
    onAction={onAction}
    sortingType={sortingType}
    setSortingType={setSortingType}
    onTableRowClick={onRowClick}
    activeRowId={activeRowId}
    tableBoxClassName={tableBoxClassName}
    scrollKey={scrollKey}
  />
);

const DetailedPortfolio = () => {
  const [searchParams, setSearchParams] =
    React.useState<IPortfoGroupDetailedSearchParams>({
      accountId: null,
    });
  const [portfoGroupName, setPortfoGroupName] = React.useState<string | null>(
    null,
  );
  const [selectedDeltaHedgeRow, setSelectedDeltaHedgeRow] =
    React.useState<IDeltaHedgeSummaryTableReport | null>(null);
  const [deltaHedgeDetailsSearchParams, setDeltaHedgeDetailsSearchParams] =
    React.useState<IPortfoSegregationDetailsSearchParams | null>(null);

  const filterChangeHandler = (name: string, value: number | string) => {
    setSearchParams((prev) => ({
      ...prev,
      [name]: value === "" || value == null ? null : Number(value),
    }));
  };

  React.useEffect(() => {
    setPortfoGroupName(null);
    setSelectedDeltaHedgeRow(null);
    setDeltaHedgeDetailsSearchParams(null);
  }, [searchParams.accountId]);

  const deltaHedgeSummaryRowClickHandler = (
    row: IDeltaHedgeSummaryTableReport,
  ) => {
    const accountId = row.accountId ?? searchParams.accountId;
    if (accountId == null || !row.baseInstrumentId) return;

    setSelectedDeltaHedgeRow(row);
    setDeltaHedgeDetailsSearchParams({
      accountId,
      assetType: row.assetType,
      insId: row.baseInstrumentId,
    });
  };

  return (
    <div className="flex flex-col flex-1 w-full gap-4 overflow-x-auto">
      <div className="flex flex-row justify-between items-end gap-3 min-w-[1200px]">
        <div className="flex flex-row items-end gap-11">
          <div className="w-[220px]">
            <UsersDropdown
              label="انتخاب کاربر"
              name="accountId"
              value={searchParams.accountId?.toString() ?? ""}
              onChange={filterChangeHandler}
              extraLabel
              getData={getPortfolioGroupAccountsDropdown}
            />
          </div>
          <span className="text-sm font-bold mb-2 whitespace-nowrap">
            گروه پرتفو: {portfoGroupName || "-"}
          </span>
          <div className="w-36">
            <FileDownloader getFile={() => getDetailedPortfolioExcel(searchParams)} />
          </div>
        </div>
      </div>

      <div className="flex flex-row items-start gap-5 w-full min-w-[1640px]">
        <div className="flex flex-col gap-8 w-[520px] shrink-0">
          <TableSection<IPortfoOverviewTableReport>
            title="تخصیص طبقات دارایی /استراتژی"
            searchParams={searchParams}
            getDataTable={getPortfoOverviewTable}
            tableBoxClassName="max-h-none overflow-visible"
            onResponse={(response) => {
              setPortfoGroupName(
                (response as IPortfoOverviewTableResponse).portfoGroupName ??
                  null,
              );
            }}
          />

          {deltaHedgeDetailsSearchParams ? (
            <div className="flex w-full flex-col gap-2 justify-start items-stretch text-right">
              <div className="w-full px-3 text-right text-sm font-bold whitespace-nowrap">
                جزئیات دارایی استراتژی: دلتاهج
              </div>
              <TableTemplate<
                IPortfoSegregationDetailsSearchParams,
                IPortfoSegregationDetailsTableReport
              >
                getDataTable={getDeltaHedgeSegregationDetailsTable}
                searchParameters={deltaHedgeDetailsSearchParams}
                tableWrapperClassname="w-full justify-start"
                TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                  <CommonTableUI
                    reports={reports}
                    onAction={onAction}
                    sortingType={sortingType}
                    setSortingType={setSortingType}
                    tableBoxClassName="max-h-[300px]"
                    footerRowClassName="delta-hedge-detail-footer"
                    tableHeaderClassName="!bg-sky-50 hover:!bg-sky-100"
                  />
                )}
                withSort
                refreshTableTime={30000}
                pagesize={200}
              />
            </div>
          ) : null}

          <div className="flex w-full flex-col gap-2 items-stretch">
            <div className="flex w-full items-center justify-start px-3 text-right text-sm font-bold whitespace-nowrap">
              تحلیل قدرت خرید
            </div>
            <TableTemplate<
              IPortfoGroupDetailedSearchParams,
              ISecuritiesBuyingPowerTableReport
            >
              getDataTable={getSecuritiesBuyingPowerTable}
              searchParameters={searchParams}
              onFetchError={(error) => {
                const message = error.response?.data?.msg;
                if (!message) return false;

                toast.error(message);
                return true;
              }}
              tableWrapperClassname="w-full justify-start"
              TableUI={(props) => (
                <SecuritiesBuyingPowerTableUI {...props} />
              )}
              withSort
              refreshTableTime={30000}
              pagesize={200}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 flex-1 min-w-[980px] justify-start items-start">
          <TableSection<ICoveredCallSummaryTableReport>
            className="order-3"
            title="خلاصه کاوردکال"
            searchParams={searchParams}
            getDataTable={getCoveredCallSummaryTable}
            tableBoxClassName="max-h-[300px]"
          />
          <TableSection<IConversionSummaryTableReport>
            className="order-4"
            title="خلاصه کانورژن"
            searchParams={searchParams}
            getDataTable={getConversionSummaryTable}
            tableBoxClassName="max-h-[300px]"
          />
          <TableSection<IDeltaHedgeSummaryTableReport>
            className="order-5"
            title="خلاصه دلتاهج"
            searchParams={searchParams}
            getDataTable={getDeltaHedgeSummaryTable}
            tableBoxClassName="max-h-[300px]"
            TableUIComponent={(props) => (
              <DeltaHedgeSummaryTableUI
                {...props}
                onRowClick={deltaHedgeSummaryRowClickHandler}
                activeRowId={selectedDeltaHedgeRow?.id}
              />
            )}
          />
        
          <TableSection<INonOptionAssetClassPortfolioTableReport>
            className="order-1"
            title="پرتفو طبقات دارایی به جز آپشن"
            searchParams={searchParams}
            getDataTable={getNonOptionAssetClassPortfolioTable}
            tableBoxClassName="max-h-[520px]"
          />
          <TableSection<INakedOptionPortfolioTableReport>
            className="order-2"
            title="پرتفو آپشن‌های Naked"
            searchParams={searchParams}
            getDataTable={getNakedOptionPortfolioTable}
            tableBoxClassName="max-h-[420px]"
            TableUIComponent={(props) => (
              <GroupTableUI {...props} groupTitleHeaderName="InstrumentName" />
            )}
          />
        </div>

      </div>

    </div>
  );
};

export default DetailedPortfolio;
